/* nella-overlay-history.js
 * Single-state overlay back-gesture coordinator.
 * Invariant: at most one overlay owns the active history entry at a time, AND at
 * most one synthetic history entry ever exists. When a second overlay opens while
 * one is already active, the existing slot is TRANSFERRED (no new pushState, no
 * history.back) rather than abandoned. Abandoning left stacked same-URL entries,
 * which exposed the white WKWebView root layer on the iOS interactive back gesture.
 *
 * Usage in each overlay:
 *   open:                 NellaOverlayHistory.push(this)
 *   manual/button close:  NellaOverlayHistory.pop(false)
 *   competing-UI close:   NellaOverlayHistory.pop(true)     — no history.back()
 *   replace (reuse slot): NellaOverlayHistory.transfer(this) — take over the active
 *                         slot BEFORE visually closing the overlay that owned it
 *   bfcache/immediate:    NellaOverlayHistory.release(this)
 *   implement:            closeFromBackGesture() — called by this helper on popstate
 */
(function () {
  'use strict';

  var _controller = null;
  var _pushed = false;
  var _cleanup = false;
  var _ignoreNextPop = false;

  function canUse() {
    return !!(
      window.history &&
      typeof window.history.pushState === 'function' &&
      typeof window.history.back === 'function'
    );
  }

  // Register this controller as the active overlay and push the single history entry.
  // No-op if something is already pushed (only one synthetic entry at a time).
  function push(controller) {
    if (!canUse() || _pushed) return;
    _controller = controller;
    _pushed = true;
    _cleanup = false;
    _ignoreNextPop = false;
    history.pushState({ nellaOverlayOpen: true }, '');
  }

  // Hand the existing active slot to a different overlay WITHOUT touching browser
  // history (no pushState, no history.back). Used when one overlay replaces another
  // (e.g. Search open -> Menu open): the new overlay reuses the single entry instead
  // of the old one abandoning it and the new one pushing a second entry.
  // Sets _ignoreNextPop so the outgoing overlay's own pop() — fired as it closes
  // visually right after this call — does not tear the slot back down.
  // Returns true if a slot existed and was transferred.
  function transfer(toController) {
    if (!canUse() || !_pushed) return false;
    _controller = toController;
    _ignoreNextPop = true;
    return true;
  }

  // Called by the overlay's close() method.
  // skipHistoryCleanup = false (manual close):  calls history.back() to pop the entry.
  // skipHistoryCleanup = true  (competing UI):  abandons the entry without history.back().
  // No-op if nothing was pushed, or if the slot was just transferred away — the single
  // pop() the outgoing overlay fires during a transfer is absorbed here, so the slot
  // (now owned by the incoming overlay) survives.
  function pop(skipHistoryCleanup) {
    if (!canUse() || !_pushed) return;
    if (_ignoreNextPop) {
      _ignoreNextPop = false;
      return;
    }
    _pushed = false;
    _controller = null;
    if (skipHistoryCleanup) return;
    _cleanup = true;
    history.back();
  }

  // Called on bfcache freeze / immediate close — abandons state without history.back().
  function release(controller) {
    if (_controller === controller) {
      _pushed = false;
      _controller = null;
      _cleanup = false;
      _ignoreNextPop = false;
    }
  }

  window.addEventListener('popstate', function () {
    // Absorb the popstate fired by our own history.back() in pop().
    if (_cleanup) {
      _cleanup = false;
      return;
    }
    // A real popstate consumes the slot; clear the one-shot transfer guard.
    _ignoreNextPop = false;
    if (!_pushed || !_controller) return;
    var c = _controller;
    _pushed = false;
    _controller = null;
    if (typeof c.closeFromBackGesture === 'function') {
      c.closeFromBackGesture();
    }
  });

  window.NellaOverlayHistory = {
    push: push,
    pop: pop,
    transfer: transfer,
    release: release
  };
})();
